/**
 * Typed client for the AI prompt library + scheduled prompts
 * (backend/api/ai_prompts_api.py, SellerMate W6.3). Read-only against ads
 * data: the only writes are to the scheduled-prompt tables.
 */
import { apiRequest } from './api'
import { keys } from './query/keys'

export type PromptCategory = { key: string; label: string; blurb: string }
export type LibraryPrompt = { id: string; category: string; title: string; prompt: string }
export type PromptLibrary = {
  categories: PromptCategory[]
  prompts: LibraryPrompt[]
}

export type Cadence = 'daily' | 'weekly'

export type ScheduledPrompt = {
  id: number
  name: string
  prompt: string
  cadence: Cadence
  weekday: number | null
  hour_et: number
  enabled: boolean
  template_key: string | null
  created_by: string
  created_at: string | null
  updated_at: string | null
  last_run_at: string | null
  next_run_at: string | null
}

export type Briefing = {
  id: number
  schedule_id: number
  schedule_name: string
  prompt: string
  trigger: 'schedule' | 'manual'
  status: 'ok' | 'error'
  answer: string | null
  error: string | null
  started_at: string | null
  finished_at: string | null
  read_at: string | null
}

export type ScheduleInput = {
  name: string
  prompt: string
  cadence: Cadence
  weekday?: number | null
  hour_et: number
  enabled?: boolean
}

/** The AI panel's query keys live in the shared registry (lib/query/keys.ts,
 *  where each documents its route and has a perf budget); re-exported here so
 *  existing imports keep working. */
export const aiKeys = keys.ai

export const aiApi = {
  library: () => apiRequest<PromptLibrary>('/api/ai/prompts'),
  schedules: () =>
    apiRequest<{ schedules: ScheduledPrompt[]; unread: number }>('/api/ai/scheduled-prompts'),
  createSchedule: (body: ScheduleInput) =>
    apiRequest<ScheduledPrompt>('/api/ai/scheduled-prompts', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateSchedule: (id: number, body: Partial<ScheduleInput>) =>
    apiRequest<ScheduledPrompt>(`/api/ai/scheduled-prompts/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteSchedule: (id: number) =>
    apiRequest<{ id: number; deleted: boolean }>(`/api/ai/scheduled-prompts/${id}`, { method: 'DELETE' }),
  runSchedule: (id: number) =>
    apiRequest<Briefing>(`/api/ai/scheduled-prompts/${id}/run`, { method: 'POST' }),
  briefings: () =>
    apiRequest<{ briefings: Briefing[]; unread: number }>('/api/ai/briefings'),
  markRead: (id: number) =>
    apiRequest<{ marked: number }>(`/api/ai/briefings/${id}/read`, { method: 'POST' }),
  markAllRead: () =>
    apiRequest<{ marked: number }>('/api/ai/briefings/read', { method: 'POST' }),
}
